import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconExclamationCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {
  PersonnelImportCounts,
  PersonnelImportOptions,
  PersonnelImportPreviewResponse,
  PersonnelImportPreviewRow,
} from 'src/api/ProactApiClient';
import {
  PersonnelImportBlankSupervisorEnum,
  PersonnelImportIssueSeverityEnum,
  PersonnelImportRowActionEnum,
  PersonnelImportRowErrorEnum,
  PersonnelImportSupervisorNotFoundEnum,
  PersonnelImportSupervisorStatusEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {RadioGroupBox} from 'src/design/atoms/RadioGroupBox';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  ImportActionPills,
  ImportActionStatColor,
  ImportSupervisorMatchLabel,
  ImportSupervisorPills,
} from './importPills';

const ISSUES_ONLY = 'issues';
const ALL_ROWS = 'all';

const PILLED_SUPERVISOR_STATUSES = [
  PersonnelImportSupervisorStatusEnum.Cleared,
  PersonnelImportSupervisorStatusEnum.NotFound,
  PersonnelImportSupervisorStatusEnum.Ambiguous,
  PersonnelImportSupervisorStatusEnum.Invalid,
  PersonnelImportSupervisorStatusEnum.VendorManager,
];

// Info issues are notices, not problems, so they must not force the Issues-only default.
const hasIssues = (row: PersonnelImportPreviewRow) =>
  row.issues.some(
    issue => issue.severity !== PersonnelImportIssueSeverityEnum.Info
  );

const ACTION_STAT_BUCKETS: {
  action: PersonnelImportRowActionEnum;
  label: string;
  count: (counts: PersonnelImportCounts) => number;
}[] = [
  {
    action: PersonnelImportRowActionEnum.Create,
    label: 'Will create',
    count: c => c.create,
  },
  {
    action: PersonnelImportRowActionEnum.Update,
    label: 'Will update',
    count: c => c.update,
  },
  {
    action: PersonnelImportRowActionEnum.Deactivate,
    label: 'Will deactivate',
    count: c => c.deactivate,
  },
  {
    action: PersonnelImportRowActionEnum.Skip,
    label: 'Skipped',
    count: c => c.skip,
  },
  {
    action: PersonnelImportRowActionEnum.Reject,
    label: 'Rejected',
    count: c => c.reject,
  },
];

// Zero buckets are hidden, as in CloseoutReviewDialog; the tile colour mirrors the row pill.
const buildActionStats = (counts: PersonnelImportCounts) =>
  ACTION_STAT_BUCKETS.map(bucket => ({
    label: bucket.label,
    value: bucket.count(counts),
    color: ImportActionStatColor(bucket.action),
  })).filter(stat => stat.value > 0);

const ISSUE_SEVERITY_COLOR: Record<PersonnelImportIssueSeverityEnum, string> = {
  [PersonnelImportIssueSeverityEnum.Error]: 'red',
  [PersonnelImportIssueSeverityEnum.Warning]: 'yellow.9',
  [PersonnelImportIssueSeverityEnum.Info]: 'dimmed',
};

const PREVIEW_COLUMNS: MRT_ColumnDef<PersonnelImportPreviewRow>[] = [
  {
    id: 'row',
    header: 'Row',
    accessorFn: row => row.row,
    size: 60,
  },
  {
    id: 'action',
    header: 'Action',
    accessorFn: row => ImportActionPills(row.action).text,
    size: 110,
    Cell: ({row}) => <Pill {...ImportActionPills(row.original.action)} />,
  },
  {
    id: 'person',
    header: 'Person',
    // The employee ID stays in the cell text so row-name matching still finds it.
    accessorFn: row =>
      [row.name, row.email, row.employeeId].filter(Boolean).join(' · '),
    size: 320,
    mantineTableBodyCellProps: {style: {whiteSpace: 'normal'}},
    Cell: ({row}) => {
      const {name, email, employeeId} = row.original;
      const detail = [email, employeeId].filter(Boolean).join(' · ');
      return (
        <Stack gap={2}>
          <Text size="sm">{name ?? '—'}</Text>
          {detail && (
            <Text size="xs" c="dimmed">
              {detail}
            </Text>
          )}
        </Stack>
      );
    },
  },
  {
    id: 'supervisor',
    header: 'Supervisor',
    accessorFn: row => row.supervisor.name ?? '',
    size: 220,
    Cell: ({row}) => {
      const {name, status, matchedBy} = row.original.supervisor;
      const matchedByLabel = ImportSupervisorMatchLabel(matchedBy);
      return (
        <Stack gap={2}>
          <Group gap="xs" wrap="nowrap">
            {name && <Text size="sm">{name}</Text>}
            {PILLED_SUPERVISOR_STATUSES.includes(status) && (
              <Pill {...ImportSupervisorPills(status)} />
            )}
          </Group>
          {matchedByLabel && (
            <Text size="xs" c="dimmed">
              {matchedByLabel}
            </Text>
          )}
        </Stack>
      );
    },
  },
  {
    id: 'issues',
    header: 'Issues',
    accessorFn: row => row.issues.map(issue => issue.message).join(' '),
    size: 420,
    grow: true,
    mantineTableBodyCellProps: {style: {whiteSpace: 'normal'}},
    Cell: ({row}) => (
      <Stack gap={2}>
        {row.original.issues.map((issue, index) => (
          <Text
            key={`${issue.message}-${index}`}
            size="xs"
            c={ISSUE_SEVERITY_COLOR[issue.severity]}>
            {issue.message}
          </Text>
        ))}
      </Stack>
    ),
  },
];

export interface ReviewStepProps {
  preview?: PersonnelImportPreviewResponse;
  isLoading: boolean;
  error?: string;
  options: PersonnelImportOptions;
  onOptionsChange: (options: PersonnelImportOptions) => void;
  /** Seeds the options card collapsed; a non-default selection opens it. */
  isDefaultOptions: boolean;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  preview,
  isLoading,
  error,
  options,
  onOptionsChange,
  isDefaultOptions,
}) => {
  const [explicitFilter, setExplicitFilter] = useState<string | null>(null);
  const [optionsOpened, {toggle: toggleOptions}] = useDisclosure(
    !isDefaultOptions
  );

  const {rows, issueRows} = useMemo(() => {
    const previewRows = preview?.rows ?? [];
    return {rows: previewRows, issueRows: previewRows.filter(hasIssues)};
  }, [preview]);

  const issueRowCount = issueRows.length;
  const effectiveFilter =
    explicitFilter ?? (issueRowCount > 0 ? ISSUES_ONLY : ALL_ROWS);
  const visibleRows = effectiveFilter === ISSUES_ONLY ? issueRows : rows;

  const tableState = useTableState({
    identifier: 'personnel-import-preview',
    columns: PREVIEW_COLUMNS,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 5},
    defaultShowColumnFilters: false,
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      // 'grid' instead of the resizing default 'grid-no-grow': the latter
      // appends an empty spacer column and ignores `grow` on Issues.
      layoutMode: 'grid',
      enableSorting: false,
      columns: PREVIEW_COLUMNS,
      data: visibleRows,
      getRowId: row => String(row.row),
      enableRowActions: false,
      enableGlobalFilter: false,
      enableColumnFilters: false,
      enableTopToolbar: false,
      enableColumnActions: false,
      enableBottomToolbar: visibleRows.length > 5,
    },
    totalCount: visibleRows.length,
    isError: false,
    isLoading: false,
    isFetching: isLoading,
    hideResetAction: true,
  });

  if (isLoading && !preview) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!preview) {
    return (
      <Text size="sm" c="dimmed">
        Upload a file and choose Review file to see what the import will do.
      </Text>
    );
  }

  const {counts, fileIssues} = preview;
  const fileErrors = fileIssues.filter(
    issue => issue.severity === PersonnelImportIssueSeverityEnum.Error
  );
  const fileWarnings = fileIssues.filter(
    issue => issue.severity === PersonnelImportIssueSeverityEnum.Warning
  );
  const fileNotices = fileIssues.filter(
    issue => issue.severity === PersonnelImportIssueSeverityEnum.Info
  );
  const actionStats = buildActionStats(counts);

  return (
    <Stack gap="md" px="xl">
      <Stack gap={2}>
        <Text size="lg" fw={600}>
          Review what this import will do
        </Text>
        <Text size="sm" c="dimmed">
          {counts.total} {counts.total === 1 ? 'row' : 'rows'} read. Nothing has
          been saved yet.
        </Text>
      </Stack>

      {fileErrors.length > 0 && (
        <Alert
          color="red"
          variant="light"
          icon={<IconExclamationCircle />}
          title="This file can't be imported yet">
          <List size="sm">
            {fileErrors.map((issue, index) => (
              <List.Item key={`${issue.message}-${index}`}>
                {issue.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      {fileWarnings.length > 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle />}
          title="Check how this file was read">
          <List size="sm">
            {fileWarnings.map((issue, index) => (
              <List.Item key={`${issue.message}-${index}`}>
                {issue.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      {fileNotices.length > 0 && (
        <Alert
          color="blue"
          variant="light"
          icon={<IconInfoCircle />}
          title="How this file will be applied">
          <List size="sm">
            {fileNotices.map((issue, index) => (
              <List.Item key={`${issue.message}-${index}`}>
                {issue.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      )}

      <SectionCard
        title="Import options"
        onHeaderClick={toggleOptions}
        rightSection={
          <IconChevronDown
            size={18}
            style={{
              transform: optionsOpened ? 'rotate(180deg)' : undefined,
              transition: 'transform 150ms',
            }}
          />
        }>
        <Collapse in={optionsOpened}>
          <Box p="md">
            <Text size="sm" c="dimmed" mb="sm">
              Changing an option re-checks the file.
            </Text>
            <Group align="start" gap="xl" wrap="wrap">
              <RadioGroupBox
                label="If a row has an error"
                value={String(options.rowsWithErrors)}
                options={[
                  {
                    id: String(PersonnelImportRowErrorEnum.SkipRows),
                    label: 'Skip that row and import the rest (recommended)',
                  },
                  {
                    id: String(PersonnelImportRowErrorEnum.CancelAll),
                    label: "Don't import anything",
                  },
                ]}
                onChangeCallback={value =>
                  onOptionsChange({
                    ...options,
                    rowsWithErrors: Number(
                      value
                    ) as PersonnelImportRowErrorEnum,
                  })
                }
              />
              <RadioGroupBox
                label="When the Supervisor column is empty"
                value={String(options.blankSupervisor)}
                options={[
                  {
                    id: String(PersonnelImportBlankSupervisorEnum.KeepExisting),
                    label:
                      'Keep the supervisor already on record (recommended)',
                  },
                  {
                    id: String(PersonnelImportBlankSupervisorEnum.Clear),
                    label: 'Remove their supervisor',
                  },
                ]}
                onChangeCallback={value =>
                  onOptionsChange({
                    ...options,
                    blankSupervisor: Number(
                      value
                    ) as PersonnelImportBlankSupervisorEnum,
                  })
                }
              />
              <RadioGroupBox
                label="When the supervisor can't be found"
                value={String(options.supervisorNotFound)}
                options={[
                  {
                    id: String(
                      PersonnelImportSupervisorNotFoundEnum.KeepExisting
                    ),
                    label:
                      'Import the row and keep the supervisor already on record (recommended)',
                  },
                  {
                    id: String(PersonnelImportSupervisorNotFoundEnum.RejectRow),
                    label: "Don't import the row",
                  },
                ]}
                onChangeCallback={value =>
                  onOptionsChange({
                    ...options,
                    supervisorNotFound: Number(
                      value
                    ) as PersonnelImportSupervisorNotFoundEnum,
                  })
                }
              />
            </Group>
          </Box>
        </Collapse>
      </SectionCard>

      <SectionCard title="Preview">
        <Box p="md">
          <Stack gap="md">
            {fileErrors.length === 0 && actionStats.length > 0 && (
              <SimpleGrid
                cols={{base: 2, sm: Math.min(actionStats.length, 5)}}>
                {actionStats.map(stat => (
                  <InlineStat
                    key={stat.label}
                    value={
                      <Text span inherit c={stat.color}>
                        {stat.value}
                      </Text>
                    }
                    label={stat.label}
                  />
                ))}
              </SimpleGrid>
            )}

            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Rows with problems are shown first.
              </Text>
              <Group justify="space-between" align="end">
                <SegmentedControl
                  value={effectiveFilter}
                  onChange={setExplicitFilter}
                  data={[
                    {
                      value: ISSUES_ONLY,
                      label: `Issues only (${issueRowCount})`,
                    },
                    {value: ALL_ROWS, label: `All rows (${rows.length})`},
                  ]}
                />
                {preview.truncated && (
                  <Text size="xs" c="dimmed">
                    Showing first {rows.length} of {counts.total} rows. The
                    counts above cover the whole file.
                  </Text>
                )}
              </Group>
            </Stack>

            {visibleRows.length === 0 ? (
              <Text size="sm" c="dimmed">
                {rows.length === 0
                  ? 'This file has no data rows to import.'
                  : 'No rows have issues. Switch to All rows to see everything in the file.'}
              </Text>
            ) : (
              table
            )}
          </Stack>
        </Box>
      </SectionCard>
    </Stack>
  );
};
